import { ApiError } from '@/services/apiClient';
import { formatLocaleDateTime } from '@/lib/localeFormat';

/** POST/PATCH /order 시 동일 프로덕트 사용 기간이 겹칠 때 서버가 509로 반환 */
export class OrderProductPeriodOverlapError extends Error {
    readonly code = 'ORDER_PRODUCT_PERIOD_OVERLAP' as const;

    constructor(
        message: string,
        public readonly existingStartAt?: string,
        public readonly existingExpiresAt?: string
    ) {
        super(message);
        this.name = 'OrderProductPeriodOverlapError';
    }
}

function asRecord(v: unknown): Record<string, unknown> | null {
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

/**
 * 본문이 OpenAPI `properties: { message, existingStartAt, existingExpiresAt }` 형태이거나
 * 동일 필드가 루트에 있는 경우 파싱합니다.
 */
export function parseOrderProductPeriodOverlapBody(data: unknown): {
    message: string;
    existingStartAt?: string;
    existingExpiresAt?: string;
} | null {
    const root = asRecord(data);
    if (!root) return null;

    const props =
        asRecord(root.properties) ??
        asRecord(root.body) ??
        root;

    const msgRaw = props.message ?? props.errorMessage;
    const message =
        typeof msgRaw === 'string' && msgRaw.trim()
            ? msgRaw.trim()
            : '동일 프로덕트 사용기간이 겹칩니다';

    const start =
        props.existingStartAt ??
        props.existing_start_at ??
        props.existingStartDate;
    const end =
        props.existingExpiresAt ??
        props.existing_expires_at ??
        props.existingExpireDate;

    const existingStartAt = typeof start === 'string' && start.trim() ? start.trim() : undefined;
    const existingExpiresAt = typeof end === 'string' && end.trim() ? end.trim() : undefined;

    return { message, existingStartAt, existingExpiresAt };
}

export function tryOrderProductPeriodOverlapFromApiError(error: unknown): OrderProductPeriodOverlapError | null {
    if (!(error instanceof ApiError) || error.status !== 509) return null;
    const parsed = parseOrderProductPeriodOverlapBody(error.data);
    if (!parsed) return null;
    return new OrderProductPeriodOverlapError(parsed.message, parsed.existingStartAt, parsed.existingExpiresAt);
}

export function formatOrderOverlapForAlert(e: OrderProductPeriodOverlapError): string {
    const lines = [e.message];
    if (e.existingStartAt) {
        lines.push(`기존 사용 시작: ${formatIsoDateTimeKo(e.existingStartAt)}`);
    }
    if (e.existingExpiresAt) {
        lines.push(`기존 사용 종료: ${formatIsoDateTimeKo(e.existingExpiresAt)}`);
    }
    return lines.join('\n\n');
}

function formatIsoDateTimeKo(iso: string): string {
    const formatted = formatLocaleDateTime(iso);
    return formatted === '-' ? iso : formatted;
}
